
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, QrCode, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase/client';
import { CheckCircle, CircleNotch, QrCode as QrCodeIcon, ShieldCheck as ShieldCheckIcon } from '@phosphor-icons/react';
import { DialogHuelle } from './immobilien/ui/DialogHuelle';

interface Rechnung {
  id: string;
  rechnungsnummer: string;
  unternehmen: string;
  brutto: number;
  waehrung: string;
  iban?: string;
  verwendungszweck?: string;
  zahlungsempfaenger?: string;
  is_unklar_source?: boolean;
}

interface PaymentQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  rechnung: Rechnung;
  /** „app“ = Dialog im App-Stil (Rechnungen-Redesign); Standard „alt“ für das Dashboard */
  huelle?: 'alt' | 'app';
  /** Fehlermeldung anzeigen (App-Stil); ohne Angabe wie bisher alert */
  melde?: (text: string) => void;
  /** Bezahlt über den Workflow setzen (markAsBezahlt inkl. Vier-Augen-Prüfung); true = erfolgreich.
   *  Ohne Angabe wie bisher direkte Status-Änderung (Dashboard). */
  onAlsBezahlt?: () => Promise<boolean>;
}

const PaymentQRModal: React.FC<PaymentQRModalProps> = ({ isOpen, onClose, onSuccess, rechnung, huelle = 'alt', melde, onAlsBezahlt }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // EPC-QR-Code Format (SEPA)
  const name = rechnung.zahlungsempfaenger || rechnung.unternehmen;
  const iban = (rechnung.iban || '').replace(/\s/g, '');
  const amount = rechnung.brutto.toFixed(2);
  const purpose = rechnung.verwendungszweck || `Rechnung ${rechnung.rechnungsnummer}`;
  
  const qrData = `BCD\n002\n1\nSCT\n\n${name}\n${iban}\nEUR${amount}\n\n\n${purpose}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;

  const handleMarkAsPaid = async () => {
    if (onAlsBezahlt) {
      // Fehler (z. B. „Vier-Augen-Freigabe erforderlich …“) meldet der Aufrufer selbst; Dialog bleibt dann offen.
      setLoading(true);
      try {
        if (await onAlsBezahlt()) onClose();
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const targetTable = rechnung.is_unklar_source ? 'rechnungen_unklar' : 'rechnungen';
      // bezahlt_timestamp existiert nur in rechnungen, nicht in rechnungen_unklar (KI-Pool)
      const updateData: { status: string; bezahlt_timestamp?: string } = { status: 'bezahlt' };
      if (!rechnung.is_unklar_source) {
        updateData.bezahlt_timestamp = new Date().toISOString();
      }
      const { error } = await supabase
        .from(targetTable)
        .update(updateData)
        .eq('id', rechnung.id);

      if (error) throw error;
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      const text = 'Fehler beim Aktualisieren des Status: ' + err.message;
      if (melde) melde(text); else alert(text);
    } finally {
      setLoading(false);
    }
  };

  if (huelle === 'app') {
    const zeile = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 14px' } as const;
    return (
      <DialogHuelle titel="Direktzahlung" untertitel="SEPA Bezahlcode" icon={QrCodeIcon} iconFarbe={{ farbe: '#fff', flaeche: '#34c759' }}
        onSchliessen={onClose} laeuft={loading} breite={440}
        fuss={
          <>
            <button type="button" onClick={handleMarkAsPaid} disabled={loading} className="inline-flex flex-1 items-center justify-center"
              style={{ height: 48, minWidth: 180, gap: 7, borderRadius: 15, border: 'none', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: '#34c759', color: '#fff', opacity: loading ? 0.6 : 1 }}>
              {loading ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <CheckCircle size={16} weight="bold" />}
              Als bezahlt markieren
            </button>
            <button type="button" onClick={onClose} className="inline-flex flex-1 items-center justify-center"
              style={{ height: 48, minWidth: 140, borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
              Fertig &amp; Schließen
            </button>
          </>
        }>
        <div className="relative self-center" style={{ width: 210, height: 210, marginTop: 10, borderRadius: 24, padding: 14, background: '#fff', boxShadow: '0 18px 40px -18px var(--shade)' }}>
          <span className="absolute inline-flex items-center" style={{ left: '50%', top: -10, transform: 'translateX(-50%)', height: 22, padding: '0 10px', borderRadius: 11, fontSize: 10.5, fontWeight: 800, background: '#34c759', color: '#fff', whiteSpace: 'nowrap' }}>Scan Ready</span>
          <img src={qrUrl} alt="SEPA QR Code" className="block h-full w-full" />
        </div>
        <span className="inline-flex items-center self-center" style={{ gap: 6, fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>
          <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: '#34c759' }} />Live-Generiert
        </span>
        <div className="overflow-hidden" style={{ borderRadius: 18, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <div style={{ ...zeile, borderBottom: '1px solid var(--line)' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)' }}>Zahlbetrag</span>
            <span style={{ fontSize: 20, fontWeight: 800 }}>{rechnung.brutto.toLocaleString('de-DE', { style: 'currency', currency: rechnung.waehrung || 'EUR' })}</span>
          </div>
          <div style={{ ...zeile, borderBottom: '1px solid var(--line)' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>Zahlungsempfänger</span>
            <span className="text-right" style={{ fontSize: 13.5, fontWeight: 700, overflowWrap: 'anywhere' }}>{name}</span>
          </div>
          <div style={zeile}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>Referenz / Zweck</span>
            <span className="text-right" style={{ fontSize: 13.5, fontWeight: 700, overflowWrap: 'anywhere' }}>{purpose}</span>
          </div>
        </div>
        <span className="flex items-center" style={{ gap: 8, fontSize: 12.5, lineHeight: 1.4, color: 'var(--ink2)' }}>
          <ShieldCheckIcon size={16} weight="fill" color="var(--ok)" className="shrink-0" />
          Banking-App öffnen &amp; Code scannen. Daten werden sicher übernommen.
        </span>
      </DialogHuelle>
    );
  }

  return createPortal(
    <div data-portal-modal="true" className="fixed inset-0 z-[10000] flex items-center justify-center p-2 lg:p-4 bg-[#0f172a]/60 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-2xl rounded-[2rem] lg:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-400 border border-white/20 max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 lg:p-8 pb-2 lg:pb-3 flex justify-between items-start sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 lg:gap-4">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-[#c4a77d]/10 flex items-center justify-center text-[#c4a77d] shadow-sm">
              <QrCode size={20} className="lg:hidden" />
              <QrCode size={24} className="hidden lg:block" />
            </div>
            <div>
              <h2 className="text-sm lg:text-base font-black text-[#0f172a] uppercase tracking-tighter leading-none">Direktzahlung</h2>
              <p className="text-[7px] lg:text-[8px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 lg:mt-1.5">SEPA Bezahlcode</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 bg-gray-50 rounded-full text-gray-300 hover:text-gray-900 transition-all shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 lg:p-10 pt-2 lg:pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 items-center">
            {/* QR Part */}
            <div className="flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-gray-100 pb-6 md:pb-0 md:pr-10">
              <div className="bg-[#fdfbf8] p-5 lg:p-8 rounded-[1.5rem] lg:rounded-[2.5rem] border border-[#c4a77d]/10 shadow-inner relative group shrink-0">
                <div className="absolute inset-0 bg-[#c4a77d]/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-[1.5rem] lg:rounded-[2.5rem] flex items-center justify-center pointer-events-none">
                    <span className="text-[7px] lg:text-[8px] font-black text-[#c4a77d] uppercase tracking-widest bg-white px-3 py-1.5 rounded-full shadow-lg">Scan Ready</span>
                </div>
                <img src={qrUrl} alt="SEPA QR Code" className="w-32 h-32 lg:w-44 lg:h-44 mix-blend-multiply" />
              </div>
              <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-100">
                 <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
                 <span className="text-[7px] text-green-600 font-black uppercase tracking-widest">Live-Generiert</span>
              </div>
            </div>

            {/* Payment Details Part */}
            <div className="space-y-4 lg:space-y-6">
              <div className="flex flex-col text-center md:text-left">
                <span className="text-[8px] lg:text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Zahlbetrag</span>
                <span className="text-xl lg:text-3xl font-black text-[#0f172a]">{rechnung.brutto.toLocaleString('de-DE', { style: 'currency', currency: rechnung.waehrung || 'EUR' })}</span>
              </div>

              <div className="bg-gray-50/50 rounded-2xl p-4 lg:p-5 border border-gray-100 space-y-3 lg:space-y-4">
                <div className="flex flex-col text-[8px] lg:text-[9px] uppercase tracking-wider">
                  <span className="font-bold text-gray-400 mb-0.5">Zahlungsempfänger</span>
                  <span className="font-black text-gray-900 truncate leading-tight">{name}</span>
                </div>
                <div className="h-px bg-gray-100"></div>
                <div className="flex flex-col text-[8px] lg:text-[9px] uppercase tracking-wider">
                  <span className="font-bold text-gray-400 mb-0.5">Referenz / Zweck</span>
                  <span className="font-black text-gray-900 truncate leading-tight">{purpose}</span>
                </div>
              </div>

              <div className="flex items-start gap-2.5 p-3.5 bg-green-50/50 rounded-xl border border-green-100/50">
                <ShieldCheck size={14} className="text-green-500 shrink-0 mt-0.5" />
                <p className="text-[7px] lg:text-[8px] font-bold text-green-700 leading-normal uppercase">
                  Banking-App öffnen & Code scannen. <br/>
                  Daten werden sicher übernommen.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-5 lg:p-8 bg-white border-t border-gray-50 flex flex-col gap-3 sticky bottom-0">
          <button 
            onClick={handleMarkAsPaid}
            disabled={loading}
            className="w-full py-4 lg:py-4.5 bg-[#c4a77d] text-white rounded-xl lg:rounded-2xl text-[10px] lg:text-[11px] font-black uppercase tracking-[0.2em] shadow-xl shadow-[#c4a77d]/20 hover:bg-[#b3966a] transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
            Als bezahlt markieren
          </button>
          
          <button 
            onClick={onClose}
            className="w-full py-3.5 bg-[#0f172a] text-[#c4a77d] rounded-xl lg:rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] shadow-xl shadow-black/10 hover:text-white transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
          >
            Fertig & Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentQRModal;
